import { LeadSourceNode, StoreSalesInfoDoc } from '@gooduncles/gu-app-schema';
import { Empty } from 'antd';
import { FC, useMemo } from 'react';

import { getLeadSourceAgentChildren, getLeadSourceChildren } from './SalesManagement';

import classes from './SourceHierarchyList.module.scss';

const KNOWN_SOURCES = ['아웃바운드', '인바운드', '기존 거래처 소개'] as const;
const KNOWN_SOURCES_SET = new Set<string>(KNOWN_SOURCES);

interface StoreEntry {
  storeName: string;
  hasOrdered: boolean;
}

function sortByOrdered<T extends StoreEntry>(stores: T[]): T[] {
  return [...stores].sort((a, b) => Number(b.hasOrdered) - Number(a.hasOrdered));
}

interface SourceGroup {
  count: number;
  byDetail: Record<string, number>;
  storesByDetailAgent: Record<string, Record<string, StoreEntry[]>>;
  flatStores: { storeName: string; detail: string; hasOrdered: boolean }[];
}

/** 트리 노드의 label 목록을 순서 배열로 추출 */
function getLabels(nodes: LeadSourceNode[] | null): string[] {
  return nodes?.map((n) => n.label) ?? [];
}

/** 트리 순서 기반 비교 함수: 트리에 있으면 해당 순서, 없으면 뒤로 */
function compareByOrder(order: string[], a: string, b: string): number {
  const idxA = order.indexOf(a);
  const idxB = order.indexOf(b);
  if (idxA !== -1 && idxB !== -1) return idxA - idxB;
  if (idxA !== -1) return -1;
  if (idxB !== -1) return 1;
  return a.localeCompare(b);
}

interface SourceHierarchyListProps {
  docs: StoreSalesInfoDoc[];
  leadSourceTree: LeadSourceNode[];
}

const SourceHierarchyList: FC<SourceHierarchyListProps> = ({ docs, leadSourceTree }) => {
  const sourceGroups = useMemo((): [string, SourceGroup][] => {
    const emptyGroup = (): SourceGroup => ({
      count: 0,
      byDetail: {},
      storesByDetailAgent: {},
      flatStores: [],
    });

    const groups = docs.reduce<Record<string, SourceGroup>>((acc, d) => {
      const source = KNOWN_SOURCES_SET.has(d.leadSource ?? '') ? d.leadSource! : '그 외';
      if (!acc[source]) acc[source] = emptyGroup();
      const group = acc[source];
      group.count += 1;

      const detail = d.leadSourceDetail ?? '미분류';
      const agent = d.leadSourceAgent ?? '기타';
      group.byDetail[detail] = (group.byDetail[detail] ?? 0) + 1;
      if (!group.storesByDetailAgent[detail]) group.storesByDetailAgent[detail] = {};
      if (!group.storesByDetailAgent[detail][agent]) group.storesByDetailAgent[detail][agent] = [];
      const hasOrdered = d.recentOrders.length > 0;
      group.storesByDetailAgent[detail][agent].push({ storeName: d.storeName, hasOrdered });
      group.flatStores.push({ storeName: d.storeName, detail, hasOrdered });

      return acc;
    }, {});

    return [
      ...KNOWN_SOURCES.filter((s) => groups[s]).map((s) => [s, groups[s]] as [string, SourceGroup]),
      ...(groups['그 외'] ? [['그 외', groups['그 외']] as [string, SourceGroup]] : []),
    ];
  }, [docs]);

  if (sourceGroups.length === 0) {
    return <Empty description='해당 월 데이터 없음' image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <div className={classes.grid}>
      {sourceGroups.map(([source, group]) => (
        <div key={source} className={classes.column}>
          <div className={classes.columnHeader}>
            <span className={classes.sourceTitle}>{source}</span>
            <span className={classes.sourceCount}>{group.count}</span>
          </div>
          <div className={classes.columnBody}>
            {source === '기존 거래처 소개' ? (
              <ul className={classes.storeList}>
                {sortByOrdered(group.flatStores).map(({ storeName, detail, hasOrdered }, i) => (
                  <li
                    key={i}
                    className={[classes.storeItem, hasOrdered ? classes.ordered : classes.notOrdered].join(' ')}
                  >
                    {storeName}
                    {detail !== '미분류' && <span className={classes.referralDetail}>({detail})</span>}
                  </li>
                ))}
              </ul>
            ) : (
              Object.entries(group.storesByDetailAgent)
                .sort(([a], [b]) => compareByOrder(getLabels(getLeadSourceChildren(leadSourceTree, source)), a, b))
                .map(([detail, agents]) => (
                  <div key={detail} className={classes.detailGroup}>
                    <div className={classes.detailTitle}>
                      {detail} {group.byDetail[detail]}건
                    </div>
                    {Object.entries(agents)
                      .sort(([a], [b]) =>
                        compareByOrder(getLabels(getLeadSourceAgentChildren(leadSourceTree, source, detail)), a, b),
                      )
                      .map(([agent, stores]) => (
                        <div key={agent} className={classes.agentGroup}>
                          <span className={classes.agentLabel}>
                            {agent} {stores.length}건
                          </span>
                          <ul className={classes.storeList}>
                            {sortByOrdered(stores).map(({ storeName, hasOrdered }, i) => (
                              <li
                                key={i}
                                className={[classes.storeItem, hasOrdered ? classes.ordered : classes.notOrdered].join(
                                  ' ',
                                )}
                              >
                                {storeName}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                  </div>
                ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SourceHierarchyList;
